// components/BusModifySearch.js
import React, { useState, useRef } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import busStations from "../../Data/BusStations.json";
import {
  RiCalendarLine,
  RiBusLine,
  RiUserLocationLine,
  RiUserLocationFill,
} from "react-icons/ri";

// Custom input component for date picker
const BusCustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <div className="input-group">
    <input
      type="text"
      className="form-control"
      value={value}
      onClick={onClick}
      ref={ref}
      readOnly
      placeholder="Select date"
    />
    <button className="btn btn-secondary" type="button" onClick={onClick}>
      <RiCalendarLine />
    </button>
  </div>
));

const ModifyBusSearch = ({ searchQuery, onSearch }) => {
  // Bus search state - initialize with existing search query
  const [fromStation, setFromStation] = useState(
    searchQuery?.fromStation || "MUMBAI"
  );
  const [toStation, setToStation] = useState(searchQuery?.toStation || "PUNE");
  const [fromStationId, setFromStationId] = useState(
    searchQuery?.fromStationCode || ""
  );
  const [toStationId, setToStationId] = useState(
    searchQuery?.toStationCode || ""
  );
  const [travelDate, setTravelDate] = useState(() => {
    if (searchQuery?.travelDate) {
      return moment(searchQuery.travelDate, "YYYY-MM-DD").toDate();
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });
  const [errors, setErrors] = useState({});

  // Station search states
  const [fromStationResults, setFromStationResults] = useState([]);
  const [toStationResults, setToStationResults] = useState([]);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  // Station search handlers
  const handleFromStationSearch = (e) => {
    const value = e.target.value;
    setFromStation(value);
    if (value.length > 0) {
      const filteredStations = busStations.stationList
        .filter((station) =>
          station.stationName.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 10);
      setFromStationResults(filteredStations);
    } else {
      setFromStationResults([]);
    }
  };

  const handleToStationSearch = (e) => {
    const value = e.target.value;
    setToStation(value);
    if (value.length > 0) {
      const filteredStations = busStations.stationList
        .filter((station) =>
          station.stationName.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 10);
      setToStationResults(filteredStations);
    } else {
      setToStationResults([]);
    }
  };

  const handleFromStationClick = () => {
    const filteredStations = busStations.stationList.slice(0, 10);
    setFromStationResults(filteredStations);
    fromInputRef.current?.select();
  };

  const handleToStationClick = () => {
    const filteredStations = busStations.stationList.slice(0, 10);
    setToStationResults(filteredStations);
    toInputRef.current?.select();
  };

  const handleFromStationSelect = (station) => {
    setFromStation(station.stationName);
    setFromStationId(station.stationId);
    setFromStationResults([]);
  };

  const handleToStationSelect = (station) => {
    setToStation(station.stationName);
    setToStationId(station.stationId);
    setToStationResults([]);
  };

  // Date handler
  const handleTravelDate = (date) => {
    setTravelDate(date);
  };

  // Validation
  const validate = () => {
    let temp = {};
    temp.fromStation = fromStation === "" ? false : true;
    temp.toStation = toStation === "" ? false : true;
    temp.travelDate = travelDate === null ? false : true;

    if (fromStation === toStation) {
      temp.sameStation = false;
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  // Search handler
  const handleSearch = (e) => {
    e.preventDefault();
    if (validate()) {
      const newSearchQuery = {
        fromStationCode: fromStationId,
        fromStation: fromStation,
        toStationCode: toStationId,
        toStation: toStation,
        travelDate: moment(travelDate).format("YYYY-MM-DD"),
      };
      onSearch(newSearchQuery);
    }
  };

  return (
    <div className="card-body bg-dark border-bottom">
      <div className="row gy-3 gx-lg-2 gx-3 align-items-end">
        {/* From Station */}
        <div className="col-3">
          <label htmlFor="fromStation" className="form-label text-white">
            FROM STATION
          </label>
          <div className="bus-form-icon bus-position-relative">
            <input
              className="form-control"
              type="text"
              placeholder="Leaving from"
              value={fromStation}
              onChange={handleFromStationSearch}
              onClick={handleFromStationClick}
              onFocus={handleFromStationClick}
              ref={fromInputRef}
            />
            <RiUserLocationLine className="bus-position-absolute bus-top-50 bus-end-0 bus-translate-middle-y bus-me-2" />
          </div>
          {errors.fromStation === false && (
            <div className="bus-validation-error">
              Please select departure station
            </div>
          )}
          {fromStationResults.length > 0 && (
            <ul className="bus-location-list bus-scrollable-list">
              {fromStationResults.map((station) => (
                <li
                  className="bus-location-list-item"
                  key={station.stationId}
                  onClick={() => handleFromStationSelect(station)}
                >
                  {station.stationName}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* To Station */}
        <div className="col-3">
          <label htmlFor="toStation" className="form-label text-white">
            TO STATION
          </label>
          <div className="bus-form-icon bus-position-relative">
            <input
              className="form-control"
              type="text"
              placeholder="Going to"
              value={toStation}
              onChange={handleToStationSearch}
              onClick={handleToStationClick}
              onFocus={handleToStationClick}
              ref={toInputRef}
            />
            <RiUserLocationFill className="bus-position-absolute bus-top-50 bus-end-0 bus-translate-middle-y bus-me-2" />
          </div>
          {errors.toStation === false && (
            <div className="bus-validation-error">
              Please select destination station
            </div>
          )}
          {errors.sameStation === false && (
            <div className="bus-validation-error">
              Departure and destination cannot be same
            </div>
          )}
          {toStationResults.length > 0 && (
            <ul className="bus-location-list bus-scrollable-list">
              {toStationResults.map((station) => (
                <li
                  className="bus-location-list-item"
                  key={station.stationId}
                  onClick={() => handleToStationSelect(station)}
                >
                  {station.stationName}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Travel Date */}
        <div className="col-2">
          <label htmlFor="travelDate" className="form-label text-white">
            JOURNEY DATE
          </label>
          <DatePicker
            selected={travelDate}
            onChange={handleTravelDate}
            customInput={<BusCustomDateInput />}
            dateFormat="dd-MMM-yyyy"
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            minDate={new Date()}
            yearDropdownItemNumber={50}
            scrollableYearDropdown
            className="form-control"
            placeholder="Journey Date"
          />
          {errors.travelDate === false && (
            <div className="bus-validation-error">
              Please select journey date
            </div>
          )}
        </div>

        {/* Search Button */}
        <div className="col-2">
          <div className="form-group mb-0">
            <button
              type="button"
              className="btn btn-danger w-100 fw-medium"
              onClick={handleSearch}
              style={{ height: "38px" }}
            >
              SEARCH
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModifyBusSearch;
